import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useStaticQuery, graphql } from 'gatsby'

import { SiteDataQuery } from '../../types/graphql-types'

type HtmlHeadProps = {
  subtitle?: string
  pageContext: unknown
}

type IntlPageContext = {
  language: string
  intl?: {
    messages?: Record<string, string>
    originalPath?: string
  }
}

const siteUrl = 'https://embodyme.com'
const japaneseOnlyPaths = new Set([
  '/compliance/',
  '/crowfunding/',
  '/piprotection/',
  '/privacy/',
  '/security/',
  '/shinkapuyoyaku/',
  '/tos/',
])
const englishOnlyPaths = new Set(['/privacy_en/', '/thanks/'])

const getContentLanguage = (routeLanguage: string, originalPath: string) => {
  if (japaneseOnlyPaths.has(originalPath)) return 'ja'
  if (englishOnlyPaths.has(originalPath)) return 'en'

  return routeLanguage
}

const HtmlHead: React.FC<HtmlHeadProps> = ({ subtitle, pageContext }) => {
  const data: SiteDataQuery = useStaticQuery(
    graphql`
      query siteData {
        site {
          siteMetadata {
            description
            title
            name
          }
        }
      }
    `,
  )

  const title = data.site?.siteMetadata?.title ?? ''
  const description = data.site?.siteMetadata?.description ?? ''
  const siteName = data.site?.siteMetadata?.name ?? ''
  const { language, intl } = pageContext as IntlPageContext
  const originalPath = intl?.originalPath ?? '/'
  const isHomePage = originalPath === '/'
  const homeTitle = intl?.messages?.meta_title ?? title
  const homeDescription = intl?.messages?.meta_description ?? description
  const pageTitle = subtitle ? `${subtitle} | ${title}` : homeTitle
  const pageDescription = isHomePage ? homeDescription : description
  const englishUrl = `${siteUrl}${originalPath}`
  const japaneseUrl = `${siteUrl}/ja${originalPath}`
  const pageUrl = language === 'ja' ? japaneseUrl : englishUrl
  const contentLanguage = getContentLanguage(language, originalPath)
  const locale = contentLanguage === 'ja' ? 'ja_JP' : 'en_US'
  const alternateLocale = contentLanguage === 'ja' ? 'en_US' : 'ja_JP'

  return (
    <Helmet htmlAttributes={{ lang: contentLanguage }}>
      <meta httpEquiv="Content-Language" content={contentLanguage} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="viewport" content="width=device-width,initial-scale=1" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:image" content="https://embodyme.com/images/ogp.png" />
      <meta property="og:locale" content={locale} />
      {isHomePage && (
        <meta property="og:locale:alternate" content={alternateLocale} />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="format-detection" content="telephone=no" />
      <link rel="canonical" href={pageUrl} />
      {isHomePage && (
        <link rel="alternate" hrefLang="en" href={englishUrl} />
      )}
      {isHomePage && (
        <link rel="alternate" hrefLang="ja" href={japaneseUrl} />
      )}
      {isHomePage && (
        <link rel="alternate" hrefLang="x-default" href={englishUrl} />
      )}
      <link rel="preconnect" href="https://fonts.gstatic.com" />
    </Helmet>
  )
}

export default HtmlHead
